import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/query-client'
import { AppShell } from '@/components/AppShell'
import HomePage from '@/pages/HomePage'
import ContactsPage from '@/pages/ContactsPage'
import ContactDetailPage from '@/pages/ContactDetailPage'
import ContactFormPage from '@/pages/ContactFormPage'
import ContactCreateMethodPage from '@/pages/ContactCreateMethodPage'
import LogInteractionPage from '@/pages/LogInteractionPage'
import { ContactsLayout } from '@/components/ContactsLayout'
import CirclesPage from '@/pages/CirclesPage'
import LabelsPage from '@/pages/LabelsPage'
import RemindersPage from '@/pages/RemindersPage'
import SettingsPage from '@/pages/SettingsPage'
import DuplicatesPage from '@/pages/DuplicatesPage'
import McpConnectPage from '@/pages/McpConnectPage'
import SignInPage from '@/pages/SignInPage'
import SignUpPage from '@/pages/SignUpPage'
import ForgotPasswordPage from '@/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/ResetPasswordPage'
import ImportContactsPage from '@/pages/ImportContactsPage'
import SearchPage from '@/pages/SearchPage'
import BillingPage from '@/pages/BillingPage'
import QuickCapturePage from '@/pages/QuickCapturePage'
import SharedContactPage from '@/pages/SharedContactPage'
import TargetsPage from '@/pages/TargetsPage'
import NetworkPage from '@/pages/NetworkPage'
import ConnectedAccountsPage from '@/pages/ConnectedAccountsPage'
import UpdatesPage from '@/pages/UpdatesPage'
import AdminPage from '@/pages/AdminPage'
import { Toaster } from 'sonner'
import { OfflineIndicator } from '@/components/OfflineIndicator'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { useSession } from '@/hooks/use-auth'
import { useEffect } from 'react'
import { useThemeStore, applyTheme } from '@/stores/theme'
import { useShareReceiver } from '@/hooks/use-share-receiver'

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useThemeStore((s) => s.theme)

  useEffect(() => {
    applyTheme(theme)
    const mq = matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      if (theme === 'system') applyTheme('system')
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  return <>{children}</>
}

function ShareReceiverProvider({ children }: { children: React.ReactNode }) {
  useShareReceiver()
  return <>{children}</>
}

function AuthGuard() {
  const { data: session, isLoading, isError } = useSession()

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    )
  }

  if (isError || !session?.user) {
    return <Navigate to="/sign-in" replace />
  }

  return <Outlet />
}

// Admin-only gate (AIM-306). Non-admins are bounced to home; the server also
// enforces admin on every /api/admin route, so this is just UX.
function AdminGuard() {
  const { data: session, isLoading } = useSession()

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    )
  }

  if (!session?.user.isAdmin) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Toaster
          position="top-center"
          closeButton
          toastOptions={{
            className: '!bg-card !text-card-foreground !border-border',
          }}
        />
        <OfflineIndicator />
        <BrowserRouter>
          <ShareReceiverProvider>
            <Routes>
              {/* Public routes */}
              <Route
                path="/mcp/connect"
                element={
                  <RouteErrorBoundary>
                    <McpConnectPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/sign-in"
                element={
                  <RouteErrorBoundary>
                    <SignInPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/sign-up"
                element={
                  <RouteErrorBoundary>
                    <SignUpPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <RouteErrorBoundary>
                    <ForgotPasswordPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <RouteErrorBoundary>
                    <ResetPasswordPage />
                  </RouteErrorBoundary>
                }
              />
              <Route
                path="/shared/:token"
                element={
                  <RouteErrorBoundary>
                    <SharedContactPage />
                  </RouteErrorBoundary>
                }
              />

              {/* Protected routes */}
              <Route element={<AuthGuard />}>
                {/* Quick Capture — full-screen, no bottom nav */}
                <Route
                  path="quick-capture"
                  element={
                    <RouteErrorBoundary>
                      <QuickCapturePage />
                    </RouteErrorBoundary>
                  }
                />
                <Route element={<AppShell />}>
                  <Route
                    index
                    element={
                      <RouteErrorBoundary>
                        <HomePage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route path="contacts" element={<ContactsLayout />}>
                    <Route
                      index
                      element={
                        <RouteErrorBoundary>
                          <ContactsPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path=":id"
                      element={
                        <RouteErrorBoundary>
                          <ContactDetailPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path="new"
                      element={
                        <RouteErrorBoundary>
                          <ContactCreateMethodPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path="new/manual"
                      element={
                        <RouteErrorBoundary>
                          <ContactFormPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path=":id/edit"
                      element={
                        <RouteErrorBoundary>
                          <ContactFormPage />
                        </RouteErrorBoundary>
                      }
                    />
                    <Route
                      path=":id/interaction"
                      element={
                        <RouteErrorBoundary>
                          <LogInteractionPage />
                        </RouteErrorBoundary>
                      }
                    />
                  </Route>
                  <Route
                    path="log-interaction"
                    element={
                      <RouteErrorBoundary>
                        <LogInteractionPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="circles"
                    element={
                      <RouteErrorBoundary>
                        <CirclesPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="labels"
                    element={
                      <RouteErrorBoundary>
                        <LabelsPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="targets"
                    element={
                      <RouteErrorBoundary>
                        <TargetsPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="network"
                    element={
                      <RouteErrorBoundary>
                        <NetworkPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="reminders"
                    element={
                      <RouteErrorBoundary>
                        <RemindersPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <RouteErrorBoundary>
                        <SettingsPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="settings/billing"
                    element={
                      <RouteErrorBoundary>
                        <BillingPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="updates"
                    element={
                      <RouteErrorBoundary>
                        <UpdatesPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="settings/connected-accounts"
                    element={
                      <RouteErrorBoundary>
                        <ConnectedAccountsPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="settings/duplicates"
                    element={
                      <RouteErrorBoundary>
                        <DuplicatesPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route element={<AdminGuard />}>
                    <Route
                      path="admin"
                      element={
                        <RouteErrorBoundary>
                          <AdminPage />
                        </RouteErrorBoundary>
                      }
                    />
                  </Route>
                  <Route
                    path="import-contacts"
                    element={
                      <RouteErrorBoundary>
                        <ImportContactsPage />
                      </RouteErrorBoundary>
                    }
                  />
                  <Route
                    path="search"
                    element={
                      <RouteErrorBoundary>
                        <SearchPage />
                      </RouteErrorBoundary>
                    }
                  />
                </Route>
              </Route>
            </Routes>
          </ShareReceiverProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  )
}
